// Zod schemas for every structured LLM output the app requests, plus a JSON-schema exporter used to
// build the `output_config`/`response_format` payloads the proxy sends upstream. See spec §6 and plan
// Task 16.
import { z } from 'zod';
import { CARDS } from '../sim/deck';
import type { CardId } from '../sim/types';

/** All card ids from the committed deck, as a zod enum (`z.enum` needs a non-empty tuple literal). */
const CARD_IDS = CARDS.map((c) => c.id) as [CardId, ...CardId[]];
export const CardIdSchema = z.enum(CARD_IDS);

export const TargetSchema = z.object({
  kind: z.enum(['point', 'graphic', 'unit', 'contact', 'none']),
  cell: z.number().int().optional(),
  graphicId: z.string().optional(),
  unitId: z.number().int().optional(),
});

export const OrderSchema = z.object({
  unitId: z.number().int(),
  card: CardIdSchema,
  target: TargetSchema,
});

/** The three advisor personas, as the schema names them; `disagreesWith` may name any one of them. */
export const PersonaSchema = z.enum(['maneuver', 'fires', 'recon']);

/**
 * Whether a play carries on what the advisor already proposed, changes its mind, or is a warning the
 * commander should act on now. The advisor is shown its own previous play and whether the player
 * executed it, so "continue" is a claim about memory, not a default (pitch 3, "a staff that argues").
 */
export const StanceSchema = z.enum(['continue', 'change', 'urgent']);
export type Stance = z.infer<typeof StanceSchema>;

export const PlaySchema = z.object({
  name: z.string().max(40).describe(
    'The play\'s name, in Title Case, at most four words, keeping designators in capitals ("Screen NAI 2", "Push to PL RED") — not '
    + '"screen the north flank with the recon platoon". It is a heading, not a sentence.',
  ),
  rationale: z.string().max(240),
  stance: StanceSchema.describe(
    'continue = the same play as last time, still right; change = a different play because the situation moved; '
    + 'urgent = act now, this refresh, or the chance is gone.',
  ),
  /**
   * The other advisor whose currently displayed play this one contradicts, if any. The user block
   * carries the other two personas' play names and one-line rationales so this is a judgement about a
   * real proposal on the rail, not an invented opponent.
   */
  disagreesWith: PersonaSchema.optional().describe(
    'Name another advisor only when your play cannot be executed together with the play shown for that '
    + 'advisor. Leave it out otherwise.',
  ),
  cited_event_ids: z.array(z.number().int()),
  orders: z.array(OrderSchema).max(6),
});

export const AssessmentSchema = z.object({
  text: z.string().max(300),
  confidence: z.enum(['low', 'medium', 'high']),
  cited_event_ids: z.array(z.number().int()),
});

export const FragoSchema = z.object({
  orders: PlaySchema.shape.orders,
  unparsed: z.string().optional(),
});

export const DebriefSchema = z.object({
  paragraphs: z.array(
    z.object({
      heading: z.string(),
      // 900, not 600: the rapporteur writes a full paragraph of prose about a two-hundred-event fight,
      // and at 600 every model answer overran the cap and was discarded for the template. The system
      // prompt states the same number so the limit is a brief rather than a trap.
      text: z.string().max(900),
      cited_event_ids: z.array(z.number().int()),
    }),
  ).max(6),
});

/**
 * Converts a zod schema to a plain-object JSON Schema for the `output_config.format.schema` /
 * `response_format.json_schema.schema` payloads. Zod 4 ships `z.toJSONSchema` directly (verified against
 * the installed 4.6.5); if a future downgrade removes it, replace this with hand-written minimal schemas
 * per export above rather than pulling in the `zod-to-json-schema` package.
 */
export function zodToJsonSchema(schema: z.ZodType): Record<string, unknown> {
  return stripUnsupportedKeywords(z.toJSONSchema(schema)) as Record<string, unknown>;
}

/**
 * JSON Schema keywords the Anthropic structured-output validator refuses, removed.
 *
 * Zod 4 emits `minimum`/`maximum` for every `z.number().int()` (the safe-integer range), and the API
 * answers `400 invalid_request_error: output_config.format.schema: For 'integer' type, properties
 * maximum, minimum are not supported`. Nothing broke visibly, because the proxy catches that 400 and
 * retries with the schema pasted into the system prompt — so every structured call in this app has
 * been silently paying an extra failed round trip and then asking the model to obey a schema in prose
 * instead of having the decoder enforce it. The bounds carried no meaning here anyway: they are the
 * float-safe integer range, not a domain rule, and `validatePlay` is what actually checks unit ids.
 */
function stripUnsupportedKeywords(node: unknown): unknown {
  if (Array.isArray(node)) return node.map(stripUnsupportedKeywords);
  if (!node || typeof node !== 'object') return node;
  const out: Record<string, unknown> = {};
  const isInteger = (node as { type?: unknown }).type === 'integer';
  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    if (key === '$schema') continue;
    if (isInteger && (key === 'minimum' || key === 'maximum'
      || key === 'exclusiveMinimum' || key === 'exclusiveMaximum')) continue;
    out[key] = stripUnsupportedKeywords(value);
  }
  return out;
}

/** Words a play name may keep lowercase inside it; the first word is always capitalised. */
const MINOR_WORDS = new Set([
  'a', 'an', 'and', 'at', 'by', 'for', 'from', 'in', 'into', 'of', 'on', 'onto', 'or', 'over',
  'the', 'through', 'to', 'up', 'via', 'with',
]);

/**
 * The abbreviations a staff officer actually writes in capitals, so "Screen NAI 2" survives the pass.
 *
 * An explicit list, not a shape test: "SUPPRESS THE RIDGE" is shouting, not three acronyms, and a rule
 * of "short and upper case means acronym" turned its `THE` into a proper noun.
 */
const ACRONYMS = new Set([
  'AA', 'C2', 'CASEVAC', 'COA', 'COP', 'EW', 'FRAGO', 'HQ', 'ISR', 'NAI', 'OBJ', 'OPORD', 'PL',
  'SITREP', 'SPOTREP', 'TF', 'UAS', 'S-2', 'S-3', 'S-4',
]);

/**
 * A play name as the rail shows it: Title Case, at most four words.
 *
 * The schema asks for this and the models mostly comply, but "mostly" across three personas and a
 * refresh every few minutes is a rail whose casing visibly drifts — "Screen north flank" beside
 * "SUPPRESS THE RIDGE" beside "Commit the reserve now, before he resets". Asking and then enforcing is
 * cheaper than either alone.
 */
export function normalizePlayName(raw: string): string {
  const words = raw.replace(/[\s_]+/g, ' ').trim().split(' ').filter((w) => w.length > 0).slice(0, 4);
  if (words.length === 0) return 'Play';
  // An all-caps name ("SUPPRESS THE RIDGE") carries no case information; a mixed-case one does.
  const mixedCase = raw !== raw.toUpperCase();
  const DESIGNATORS = new Set(['PL', 'OBJ', 'AXIS', 'NAI', 'AA']);
  const titled = words
    .map((w, i) => {
      const bare = w.replace(/[^A-Za-z0-9-]/g, '');
      // Keep acronyms and short all-caps tokens the model meant as designators: PL, NAI, OBJ, ISR, UAS, RED, BLUE.
      if (ACRONYMS.has(bare.toUpperCase())) return bare.toUpperCase() + w.slice(bare.length);
      if (mixedCase && bare.length >= 2 && bare.length <= 5 && bare === bare.toUpperCase() && /[A-Z]/.test(bare)) return w;
      // A bare number ("2" in "Screen NAI 2") has no case to fix.
      if (/^[0-9]+$/.test(bare)) return w;
      const lower = w.toLowerCase();
      if (i > 0 && MINOR_WORDS.has(lower.replace(/[^a-z]/g, ''))) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    });
  // The word after a designator is a name: "PL RED", "OBJ COBALT", "AXIS NORTH", "NAI 2".
  for (let i = 1; i < titled.length; i++) {
    if (DESIGNATORS.has(titled[i - 1].replace(/[^A-Za-z]/g, '').toUpperCase())) titled[i] = titled[i].toUpperCase();
  }
  return titled.join(' ');
}
